import { Container } from 'pixi.js';
import type { App } from '../../app/App';
import { Disposer } from './Disposer';
import type { ViewportInfo } from './Viewport';

/**
 * A game screen: a PixiJS container for animated/game objects plus a DOM root
 * for menus and forms. Everything a scene registers on `this.d` is released
 * when it is destroyed.
 */
export abstract class Scene<P = void> {
  readonly root = new Container();
  readonly dom: HTMLElement;
  protected readonly d = new Disposer();
  /** Set on destroy; async animation sequences must stop when it is true. */
  protected isDestroyed = false;

  constructor(
    protected readonly app: App,
    className: string,
  ) {
    this.dom = document.createElement('section');
    this.dom.className = `scene ${className}`;
    this.root.label = className;
  }

  /** Build DOM and game objects. Called before the transition starts. */
  abstract enter(params: P): void | Promise<void>;

  /** Called once the incoming transition has finished. */
  shown(): void {}

  /** Fixed-step simulation update (ms). */
  update(_dt: number): void {}

  /** Viewport changed; reposition Pixi objects to their DOM anchors. */
  layout(_vp: ViewportInfo): void {}

  /** Return false to block leaving (e.g. unsaved drawing). */
  canLeave(): boolean | Promise<boolean> {
    return true;
  }

  destroy(): void {
    this.isDestroyed = true;
    this.d.dispose();
    this.app.tweens.kill(this.root);
    this.root.destroy({ children: true });
    this.dom.remove();
  }

  /** Rect of a DOM anchor element in canvas coordinates (CSS px). */
  protected anchor(el: Element): { x: number; y: number; w: number; h: number; cx: number; cy: number } {
    const r = el.getBoundingClientRect();
    return { x: r.left, y: r.top, w: r.width, h: r.height, cx: r.left + r.width / 2, cy: r.top + r.height / 2 };
  }
}
